import { useState, useEffect } from 'react';
import AnalyticsApiService from '../services/analyticsApi';
import UserAnalyticsApiService from '../services/userAnalyticsApi';

interface EndpointTest {
  name: string;
  endpoint: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
  data?: any;
}

export function BackendConnectionTest() {
  const [tests, setTests] = useState<EndpointTest[]>([
    { name: 'Live Metrics', endpoint: '/api/v1/analytics/live', status: 'pending' },
    { name: 'Daily Metrics', endpoint: '/api/v1/analytics/daily', status: 'pending' },
    { name: 'Response Time', endpoint: '/api/v1/analytics/response-time', status: 'pending' },
    { name: 'Group Chats', endpoint: '/api/v1/analytics/group-chats', status: 'pending' },
    { name: 'User Quick Analytics', endpoint: '/api/v1/analytics/users/{user_id}/quick', status: 'pending' },
    { name: 'Conversation PFPs', endpoint: '/api/v1/analytics/conversations/{id}/pfps', status: 'pending' },
    { name: 'SSE Connection', endpoint: '/api/v1/analytics/conversations/{id}/events', status: 'pending' },
  ]);

  const runTests = async () => {
    // Test Live Metrics
    try {
      const liveMetrics = await AnalyticsApiService.getLiveMetrics();
      updateTest('Live Metrics', 'success', 'Connected', liveMetrics);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      updateTest('Live Metrics', 'error', msg);
    }

    // Test Daily Metrics
    try {
      const dailyMetrics = await AnalyticsApiService.getDailyMetrics(7);
      updateTest('Daily Metrics', 'success', `Received ${dailyMetrics.length} days of data`, dailyMetrics);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      updateTest('Daily Metrics', 'error', msg);
    }

    // Test Response Time
    try {
      const responseTime = await AnalyticsApiService.getResponseTimeMetrics(24);
      updateTest('Response Time', 'success', `Received ${responseTime.length} metrics`, responseTime);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      updateTest('Response Time', 'error', msg);
    }

    // Test Group Chats
    try {
      const groupChats = await AnalyticsApiService.getGroupChatMetrics();
      updateTest('Group Chats', 'success', 'Connected', groupChats);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      updateTest('Group Chats', 'error', msg);
    }

    // Test User Quick Analytics
    try {
      const userAnalytics = await UserAnalyticsApiService.getUserAnalytics('test-user-123');
      updateTest('User Quick Analytics', 'success', 'Connected', userAnalytics);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      updateTest('User Quick Analytics', 'error', msg);
    }

    // Test Conversation PFPs
    try {
      const pfps = await AnalyticsApiService.getConversationPFPs(1);
      updateTest('Conversation PFPs', 'success', `Received ${pfps.length} profile pictures`, pfps);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      updateTest('Conversation PFPs', 'error', msg);
    }

    // Test SSE Connection
    try {
      const eventSource = AnalyticsApiService.createEventSource('1');
      let connected = false;
      
      eventSource.onopen = () => {
        connected = true;
        updateTest('SSE Connection', 'success', 'SSE Connected');
      };
      
      eventSource.onerror = (error) => {
        if (!connected) {
          updateTest('SSE Connection', 'error', 'SSE Connection Failed');
        }
        eventSource.close();
      };

      // Give it 3 seconds to connect
      setTimeout(() => {
        if (!connected) {
          updateTest('SSE Connection', 'error', 'SSE Connection Timeout');
          eventSource.close();
        }
      }, 3000);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      updateTest('SSE Connection', 'error', msg);
    }
  };

  const updateTest = (name: string, status: 'success' | 'error', message?: string, data?: any) => {
    setTests(prev => prev.map(test => 
      test.name === name ? { ...test, status, message, data } : test
    ));
  };

  useEffect(() => {
    runTests();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '⏳';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      default: return 'text-yellow-600';
    }
  };

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 p-6 z-50">
      <h3 className="text-lg font-semibold mb-4">Backend Connection Test</h3>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {tests.map((test) => (
          <div key={test.name} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50/50">
            <span className="text-xl mt-0.5">{getStatusIcon(test.status)}</span>
            <div className="flex-1">
              <div className="font-medium">{test.name}</div>
              <div className="text-xs text-gray-500">{test.endpoint}</div>
              {test.message && (
                <div className={`text-sm mt-1 ${getStatusColor(test.status)}`}>
                  {test.message}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={runTests}
        className="mt-4 w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
      >
        Re-run Tests
      </button>
    </div>
  );
}
